/**
 * Authentication Service
 * Handles JWT token management and authentication
 */

import api from '../API/api';

const AUTH_TOKEN_KEY = 'authToken';
const USER_DATA_KEY = 'userData';

/**
 * Login with email and password
 * Returns user data and stores JWT token
 */
export const login = async (email, password) => {
  try {
    const response = await fetch(api.authLogin, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for cookies
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    // Store token and user data
    if (data.token) {
      localStorage.setItem(AUTH_TOKEN_KEY, data.token);
    }

    if (data.user) {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(data.user));
    }

    return {
      success: true,
      token: data.token,
      user: data.user,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'An error occurred during login',
    };
  }
};

/**
 * Register a new user
 */
export const register = async (email, password, firstName, lastName) => {
  try {
    const response = await fetch(api.authRegister, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        firstName,
        lastName,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Registration failed');
    }

    return {
      success: true,
      message: data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'An error occurred during registration',
    };
  }
};

/**
 * Logout - clear token and user data
 */
export const logout = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
};

/**
 * Get stored JWT token
 */
export const getToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Get stored user data
 */
export const getUserData = () => {
  const userData = localStorage.getItem(USER_DATA_KEY);
  return userData ? JSON.parse(userData) : null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = getToken();
  return !!token;
};

/**
 * Check if user has specific role
 */
export const hasRole = (role) => {
  const user = getUserData();
  return user && user.role === role;
};

/**
 * Check if user is Admin
 */
export const isAdmin = () => {
  return hasRole('Admin');
};

/**
 * Check if user is User
 */
export const isUser = () => {
  return hasRole('User');
};

/**
 * Make authenticated API request
 * Automatically adds JWT token to headers
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  // If unauthorized, clear token and redirect to login
  if (response.status === 401 || response.status === 403) {
    logout();
    window.location.href = '/admin/dashboard';
  }

  return response;
};

export default {
  login,
  register,
  logout,
  getToken,
  getUserData,
  isAuthenticated,
  hasRole,
  isAdmin,
  isUser,
  authenticatedFetch,
};
